// src/components/attachments/AttachmentsPanel.js

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { Paperclip, Trash2, Loader2, Download } from "lucide-react";

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(1)} ${sizes[i]}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  let d = null;

  if (value.toDate) d = value.toDate();
  else if (value.seconds) d = new Date(value.seconds * 1000);
  else if (typeof value === "number") d = new Date(value);
  else if (typeof value === "string") d = new Date(value);

  if (!d || Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "2-digit",
  });
};

const getFileExtension = (fileName) => {
  if (!fileName || typeof fileName !== "string") return null;
  const parts = fileName.split(".");
  if (parts.length < 2) return null;
  return (parts.pop() || "").toLowerCase() || null;
};

const getFileCategory = (fileName) => {
  const ext = (getFileExtension(fileName) || "").toLowerCase();

  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  if (["doc", "docx", "txt", "rtf"].includes(ext)) return "doc";
  if (["xls", "xlsx", "csv"].includes(ext)) return "spreadsheet";
  if (["ppt", "pptx"].includes(ext)) return "presentation";
  return "file";
};

const buildSearchText = ({
  fileName,
  accountName,
  parentTitle,
  parentStatus,
  assignedToName,
  parentType,
}) => {
  return [fileName, accountName, parentTitle, parentStatus, assignedToName, parentType]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const AttachmentsPanel = ({
  companyId,
  parentType, // e.g. "sampleRequest" or "task"
  parentId, // parent record doc id
  currentUserId,
  readOnly = false,
  title = "Attachments",

  // ✅ Parent metadata (optional but recommended)
  accountId = null,
  accountName = null,
  parentTitle = null, // e.g. productName or task title
  parentStatus = null, // e.g. order status or task status
  assignedToId = null,
  assignedToName = null,

  // ✅ Pagination
  pageSize = 50,
}) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const [lastDocSnap, setLastDocSnap] = useState(null);

  const fileInputRef = useRef(null);

  const db = getFirestore();
  const storage = getStorage();

  const buildBaseQuery = useCallback(() => {
    const attachmentsCol = collection(db, "attachments");

    return query(
      attachmentsCol,
      where("companyId", "==", companyId),
      where("parentType", "==", parentType),
      where("parentId", "==", parentId),
      orderBy("createdAt", "desc")
    );
  }, [db, companyId, parentType, parentId]);

  const loadFirstPage = useCallback(async () => {
    if (!companyId || !parentType || !parentId) {
      setAttachments([]);
      setLoading(false);
      setHasMore(false);
      setLastDocSnap(null);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const base = buildBaseQuery();
      const q = query(base, limit(pageSize));
      const snap = await getDocs(q);

      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const last = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;

      setAttachments(list);
      setLastDocSnap(last);
      setHasMore(snap.docs.length === pageSize);
    } catch (err) {
      console.error("[AttachmentsPanel] Error loading attachments:", err);
      setError("Could not load attachments.");
      setAttachments([]);
      setHasMore(false);
      setLastDocSnap(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, parentType, parentId, buildBaseQuery, pageSize]);

  const loadMore = useCallback(async () => {
    if (!hasMore || !lastDocSnap) return;
    if (!companyId || !parentType || !parentId) return;

    setLoadingMore(true);
    setError("");

    try {
      const base = buildBaseQuery();
      const q = query(base, startAfter(lastDocSnap), limit(pageSize));
      const snap = await getDocs(q);

      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const last = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;

      setAttachments((prev) => [...prev, ...list]);
      setLastDocSnap(last);
      setHasMore(snap.docs.length === pageSize);
    } catch (err) {
      console.error("[AttachmentsPanel] Error loading more attachments:", err);
      setError("Could not load more attachments.");
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, lastDocSnap, companyId, parentType, parentId, buildBaseQuery, pageSize]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    if (!companyId || !parentType || !parentId) return;

    setUploading(true);
    setError("");

    try {
      for (const file of files) {
        const safeName = file.name.replace(/\s+/g, "_");
        const storagePath = `attachments/${companyId}/${parentType}/${parentId}/${Date.now()}_${safeName}`;

        const fileRef = ref(storage, storagePath);
        await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(fileRef);

        const fileNameLower = (file.name || "").toLowerCase();
        const fileExtension = getFileExtension(file.name);
        const fileCategory = getFileCategory(file.name);

        const accountNameClean = (accountName || "").trim() || null;
        const parentTitleClean = (parentTitle || "").trim() || null;
        const assignedToNameClean = (assignedToName || "").trim() || null;

        const accountNameLower = accountNameClean ? accountNameClean.toLowerCase() : null;
        const parentTitleLower = parentTitleClean ? parentTitleClean.toLowerCase() : null;
        const assignedToNameLower = assignedToNameClean ? assignedToNameClean.toLowerCase() : null;

        const searchText = buildSearchText({
          fileName: file.name,
          accountName: accountNameClean,
          parentTitle: parentTitleClean,
          parentStatus: parentStatus || null,
          assignedToName: assignedToNameClean,
          parentType,
        });

        await addDoc(collection(db, "attachments"), {
          companyId,
          parentType,
          parentId,

          fileName: file.name,
          fileNameLower,
          fileExtension,
          fileCategory,

          accountId: accountId || null,
          accountName: accountNameClean,
          accountNameLower,

          parentTitle: parentTitleClean,
          parentTitleLower,
          parentStatus: parentStatus || null,

          assignedToId: assignedToId || null,
          assignedToName: assignedToNameClean,
          assignedToNameLower,

          searchText,

          storagePath,
          downloadURL,
          contentType: file.type || null,
          size: file.size || null,

          createdBy: currentUserId || null,
          createdAt: serverTimestamp(),
        });
      }

      await loadFirstPage();
    } catch (err) {
      console.error("[AttachmentsPanel] Error uploading attachment(s):", err);
      setError(err?.message || "Failed to upload attachments.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (attachment) => {
    if (!attachment?.id) return;

    const confirmDelete = window.confirm(
      `Delete "${attachment.fileName}"? This cannot be undone.`
    );
    if (!confirmDelete) return;

    setError("");

    try {
      if (attachment.storagePath) {
        const fileRef = ref(storage, attachment.storagePath);
        try {
          await deleteObject(fileRef);
        } catch (storageErr) {
          console.warn(
            "[AttachmentsPanel] Could not delete from storage (will still remove Firestore doc):",
            storageErr
          );
        }
      }

      await deleteDoc(doc(db, "attachments", attachment.id));
      setAttachments((prev) => prev.filter((att) => att.id !== attachment.id));
    } catch (err) {
      console.error("[AttachmentsPanel] Error deleting attachment:", err);
      setError("Failed to delete attachment.");
    }
  };

  const totalCount = attachments.length;

  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-blue-100 flex items-center justify-center">
            <Paperclip className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <h3 className="text-xs font-semibold text-gray-800">{title}</h3>
            <p className="text-[11px] text-gray-500">
              {loading
                ? "Loading…"
                : totalCount === 0
                ? "No files yet"
                : `${totalCount} file${totalCount === 1 ? "" : "s"}`}
            </p>
          </div>
        </div>

        {!readOnly && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFilesSelected}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || !companyId || !parentId}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white border border-gray-300 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Paperclip className="w-3 h-3" />
                  Upload files
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-2 text-[11px] text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">
          {error}
        </div>
      )}

      <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
        {!loading && attachments.length === 0 && (
          <div className="text-[11px] text-gray-400">
            Attach product specs, photos, signed tickets, or other related files here.
          </div>
        )}

        {attachments.map((att) => (
          <div
            key={att.id}
            className="flex items-center justify-between text-[11px] bg-white border border-gray-200 rounded-md px-2 py-1.5"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Download className="w-3 h-3 text-gray-500" />
              </div>
              <div className="min-w-0">
                {att.downloadURL ? (
                  <a
                    href={att.downloadURL}
                    target="_blank"
                    rel="noreferrer"
                    className="block text-gray-800 truncate hover:underline"
                  >
                    {att.fileName || "Attachment"}
                  </a>
                ) : (
                  <span className="block text-gray-800 truncate">
                    {att.fileName || "Attachment"}
                  </span>
                )}
                <div className="flex flex-wrap gap-2 text-[10px] text-gray-500">
                  <span>{formatBytes(att.size)}</span>
                  <span>•</span>
                  <span>{formatDate(att.createdAt)}</span>
                  {att.fileCategory ? (
                    <>
                      <span>•</span>
                      <span className="uppercase">{att.fileCategory}</span>
                    </>
                  ) : null}
                  {att.fileExtension ? (
                    <>
                      <span>•</span>
                      <span className="uppercase">{att.fileExtension}</span>
                    </>
                  ) : null}
                </div>
              </div>
            </div>

            {!readOnly && (
              <button
                type="button"
                onClick={() => handleDelete(att)}
                className="ml-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded p-1 flex-shrink-0"
                title="Delete attachment"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      {!loading && hasMore && (
        <div className="mt-2 flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white border border-gray-300 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {loadingMore ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Loading…
              </>
            ) : (
              "Load more"
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default AttachmentsPanel;
